/**
 * Reads the customer-portal catalogue routes' structured error detail
 * (`{reason_code, message}` — modules/catalogue_module/api/routes/
 * _customer_order_errors.py) off an `ApiError`, falling back to the plain
 * string detail for the handful of responses that are still a bare string
 * (404 "Not found", 400 item_id/configuration validation).
 *
 * `client.ts`'s error path JSON-stringifies a structured `detail` object
 * into `ApiError.detail` (so existing plain-string call sites still get a
 * readable fallback), which makes a reason-coded message unreadable
 * ("{\"reason_code\":...}") if rendered as-is — this reads the original
 * parsed body (`ApiError.body`) instead, which both adapters populate the
 * same way (real: the actual JSON response; mock: a same-shaped `body`
 * passed to `ApiError`'s constructor).
 */

import { ApiError } from "../data/api/errors";

export type CustomerOrderReasonCode =
  | "ordering_unavailable"
  | "delivery_address_unavailable"
  | "invalid_configuration"
  | "price_blocked"
  | string;

export interface CustomerOrderErrorDetail {
  reasonCode: CustomerOrderReasonCode | null;
  message: string;
}

function structuredDetail(
  body: unknown,
): { reason_code?: string; message?: string } | null {
  if (body && typeof body === "object" && "detail" in body) {
    const detail = (body as { detail?: unknown }).detail;
    if (detail && typeof detail === "object") {
      return detail as { reason_code?: string; message?: string };
    }
  }
  return null;
}

/** The customer-safe message + reason code for a failed ordering call, or
 *  `fallback` when `err` carries nothing more specific (a network drop, a
 *  non-ApiError). Prefer this over `mutationErrorMessage` on the ordering
 *  surface so a `price_blocked` 409 reads as its own sentence
 *  rather than a JSON blob. */
export function customerOrderErrorDetail(
  err: unknown,
  fallback: string,
): CustomerOrderErrorDetail {
  if (err instanceof ApiError) {
    const structured = structuredDetail(err.body);
    if (structured?.message) {
      return {
        reasonCode: structured.reason_code ?? null,
        message: structured.message,
      };
    }
    if (err.detail) return { reasonCode: null, message: err.detail };
  }
  if (err instanceof Error && err.message) {
    return { reasonCode: null, message: err.message };
  }
  return { reasonCode: null, message: fallback };
}

/** True when the failure is the "needs approval before ordering online"
 *  guardrail (`PriceBlockedByGuardrailError` -> 409 on this boundary) —
 *  the real backend's equivalent of the pre-revision mock's `"blocked"`
 *  price basis (see customer-portal-ordering's frontend spec §3). */
export function isPriceBlockedError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 409 &&
    structuredDetail(err.body)?.reason_code === "price_blocked"
  );
}
